import { isArray, isNil, size } from 'lodash'

import {
  BLANK_ALLOWLIST_ROW,
  LIBRARY_TYPE,
  entrySelectsAList,
  isSiteWideSelector,
  normalizedLibrary,
  sameSelector,
  sameSite,
  sitePathOf,
  toServerEntry,
} from '@/views/Organizations/components/SharePointConnectionForm.helpers'

export const SITE_ASSETS_LIBRARY = 'Site Assets'

// The backend's token for the library: lowercase with all whitespace removed.
const SITE_ASSETS_TOKEN = 'siteassets'
const DISMISSED_KEY_PREFIX = 'knowledgeBaseDismissedNotebooks'

export const isSiteAssetsLibrary = (name) =>
  normalizedLibrary(name).toLowerCase().replace(/\s+/g, '') === SITE_ASSETS_TOKEN

const onSite = (record, sitePath) => sameSite(record, { site_path: sitePath })

export const wholeSiteEntryFor = (allowlist, sitePath) =>
  allowlist.find(
    (entry) =>
      !entrySelectsAList(entry) && isSiteWideSelector(entry.library_name) && onSite(entry, sitePath)
  )

const hasSiteAssetsEntry = (allowlist, sitePath) =>
  allowlist.some(
    (entry) =>
      !entrySelectsAList(entry) &&
      onSite(entry, sitePath) &&
      isSiteAssetsLibrary(entry.library_name)
  )

// Judged against the persisted allowlist, so the nudge clears the moment a Site
// Assets row is saved instead of waiting for the nightly sync to prune it.
export const pendingNotebooks = (connection) => {
  // Paused or erased connections do not sync, so their stored findings go stale.
  if (connection.active !== true) {
    return []
  }
  // Older agent-gpt sends no unindexed_notebooks; none is what a missing key meant there.
  const notebooks = connection.unindexed_notebooks ?? []
  return notebooks.filter((notebook) => {
    const sitePath = sitePathOf(notebook)
    return (
      !isNil(wholeSiteEntryFor(connection.allowlist, sitePath)) &&
      !hasSiteAssetsEntry(connection.allowlist, sitePath)
    )
  })
}

export const notebooksOnSite = (notebooks, sitePath) =>
  notebooks.filter((notebook) => onSite(notebook, sitePath))

export const notebookKey = (notebook) => `${notebook.site_path}\n${notebook.name}`

const dismissedStorageKey = (connectionId) => `${DISMISSED_KEY_PREFIX}:${connectionId}`

// Storage can be missing, blocked or hold junk; none of that may break the panel,
// so anything unreadable means nothing is dismissed.
export const readDismissedNotebooks = (connectionId) => {
  try {
    const keys = JSON.parse(window.localStorage.getItem(dismissedStorageKey(connectionId)))
    return isArray(keys) ? keys : []
  } catch (storageError) {
    return []
  }
}

export const writeDismissedNotebooks = (connectionId, keys) => {
  try {
    window.localStorage.setItem(dismissedStorageKey(connectionId), JSON.stringify(keys))
  } catch (storageError) {
    // ignore quota / privacy errors
  }
}

// What the connection card counts and the banner offers: pending, minus the dismissed keys.
export const undismissedNotebooks = (connection, dismissedKeys) =>
  pendingNotebooks(connection).filter((notebook) => !dismissedKeys.includes(notebookKey(notebook)))

// The new row takes the site's playbooks and nothing else (no weight, no lists setting).
export const siteAssetsRowFor = (siteEntry) => ({
  ...BLANK_ALLOWLIST_ROW,
  site_path: sitePathOf(siteEntry),
  selector_type: LIBRARY_TYPE,
  name: SITE_ASSETS_LIBRARY,
  // The server omits the key on an unscoped entry.
  config_cids: siteEntry.config_cids || [],
})

// True when the table is exactly the persisted allowlist, so an insert built from it cannot
// drop or resurrect a row. Site paths compare exactly; a blank-named library row still matches.
export const tableMatchesServer = (formRows, allowlist) =>
  size(formRows) === size(allowlist) &&
  formRows.every((row, index) => sameSelector(allowlist[index], toServerEntry(row)))
